// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { capitalize, isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  DataListItem,
  PagingSidePanelType,
  Radio,
  RadioOption,
  SidePanel,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { TAG_ENABLED_NETWORKS } from 'constants/General'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Channel } from 'types/channels'
import { CurrencyInterface } from 'types/wallets'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getSelectedAddress, getSelectedTag } from '../utils'
import { ChannelsQRCode } from './ChannelsQRCode'

export interface ChannelDetailsProps {
  isOpen: boolean
  onClose: () => void
  paging: PagingSidePanelType
  channelDetails: Channel
  currencies?: CurrencyInterface[]
}

export const ChannelsSidePanel = ({
  isOpen,
  onClose,
  paging,
  channelDetails,
  currencies,
}: ChannelDetailsProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const [selectedProtocol, setSelectedProtocol] = useState(channelDetails?.protocol)
  const selectedAddress = getSelectedAddress(channelDetails, selectedProtocol)
  const selectedTag = getSelectedTag(channelDetails, selectedProtocol)
  const isStellarProtocol = [TAG_ENABLED_NETWORKS.STELLAR, TAG_ENABLED_NETWORKS.XLM].some(s =>
    selectedProtocol?.toLowerCase()?.includes(s.toLowerCase())
  )

  const isMultiProtocolCurrency = !isEmpty(channelDetails?.alternatives)

  const selectedCurrencyNetwork = currencies?.find(
    network => network?.code === channelDetails?.payCurrency
  )

  const protocolItems = useMemo<RadioOption[]>(() => {
    if (!isMultiProtocolCurrency) return []
    return [
      { label: channelDetails?.protocol, value: channelDetails?.protocol },
      ...(channelDetails?.alternatives?.map(p => ({ label: p?.protocol, value: p?.protocol })) ||
        []),
    ]
  }, [isMultiProtocolCurrency, channelDetails])

  useEffect(() => {
    setSelectedProtocol(channelDetails?.protocol)
  }, [channelDetails, isOpen])

  return (
    <SidePanel
      id="side-panel-channel-details"
      topOffset={bannerSpacing}
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={channelDetails?.reference}
      onActiveRowChange={highlightRowBasedOnId}
      header={
        <>
          {!!channelDetails?.contact && (
            <Box flex gap={16} paddingB={12}>
              <Avatar size="large" type="entity" name={channelDetails?.contact?.name ?? ''} />
              <Box>
                <span className="text-foreground-subdued text-body-regular">
                  {channelDetails?.contact?.name}
                </span>
                <h2 className="text-foreground-prominent text-h2">
                  {channelDetails?.contact?.description}
                </h2>
              </Box>
            </Box>
          )}

          <DataListItem
            id="channel-link"
            title={t('payments.channelPayments.channelLink')}
            body={ellipseMiddleText(channelDetails?.redirectUrl, 30, 6)}
            copyText={channelDetails?.redirectUrl}
            externalLink={channelDetails?.redirectUrl}
          />
          <DataListItem
            id="wallet-description"
            title={t('wallet')}
            body={channelDetails?.walletDescription}
          />
          <DataListItem
            id="pay-currency"
            title={t('payments.channelPayments.payCurrency')}
            body={channelDetails?.payCurrency}
            BodyAvatar={<Avatar size="small" name={channelDetails?.payCurrency ?? ''} />}
          />
        </>
      }
      body={
        <Box className="overflow-x-hidden">
          <Box className="flex flex-col">
            <h2 className="text-foreground-prominent text-h2">
              {t('payments.channelPayments.channelDetails')}
            </h2>
            <span className="text-foreground-prominent text-label">
              {t('payments.channelPayments.scanQRCode')}
            </span>
            <Box className="mt-6 overflow-x-auto">
              {isMultiProtocolCurrency && (
                <Radio
                  label={t('payments.channelPayments.selectProtocol')}
                  options={protocolItems}
                  value={selectedProtocol}
                  onChange={setSelectedProtocol}
                />
              )}
            </Box>
            <Box className="flex flex-col items-center mt-6">
              <ChannelsQRCode
                displayAddress={selectedAddress}
                selectedCurrencyCode={channelDetails?.payCurrency?.toLowerCase()}
                protocol={
                  selectedProtocol?.toLowerCase() || channelDetails?.payCurrency?.toLowerCase()
                }
                isMultiProtocolCurrency={isMultiProtocolCurrency}
              />
            </Box>
          </Box>
          <DataListItem
            id="wallet-address"
            title={t('payments.channelPayments.sendWalletAddress', {
              currencyCode: channelDetails?.payCurrency,
            })}
            body={selectedAddress}
            copyText={selectedAddress}
            layout="vertical"
          />
          {selectedTag && (
            <DataListItem
              id="channel-tag"
              title={isStellarProtocol ? t('formFields.memo') : t('formFields.destinationTag')}
              body={selectedTag}
              copyText={selectedTag}
            />
          )}
          <Box className="mt-6">
            <Callout
              variant="info"
              GhostButton={
                <Button
                  onClick={() =>
                    window.open(
                      process.env.SEND_FUNDS_TO_WRONG_NETWORK,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  {t('learnMore')}
                </Button>
              }
            >
              {channelDetails?.alternatives?.length > 0 ? (
                <span className="text-foreground-subdued text-body-regular">
                  {t('payments.channelPayments.callOutWarningProtocol', {
                    currencyCode: channelDetails?.payCurrency,
                    protocol: selectedProtocol || channelDetails?.payCurrency,
                  })}
                </span>
              ) : (
                <span className="text-foreground-subdued text-body-regular">
                  {t('payments.channelPayments.callOutWarningNetwork', {
                    currencyCode: channelDetails?.payCurrency,
                    network:
                      capitalize(channelDetails?.network ?? '') || selectedCurrencyNetwork?.name,
                  })}
                </span>
              )}
            </Callout>
          </Box>
        </Box>
      }
    />
  )
}
